import OpenAI, {
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';
import { supabase } from '../lib/supabase';

// Initialize OpenAI client
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

/**
 * Maps OpenAI API error types to user-friendly error messages.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

export const aiCaseStudyService = {
  /**
   * Transform a raw client review into a formatted case study with compliance metrics and ROI calculations
   */
  async transformReviewToCaseStudy(reviewId) {
    try {
      // 1. Get review data with company information
      const { data: review, error: reviewError } = await supabase
        ?.from('client_reviews')
        ?.select(`
          *,
          company_settings (
            company_name,
            industry,
            organization_type,
            employee_count
          )
        `)
        ?.eq('id', reviewId)
        ?.single();

      if (reviewError) throw reviewError;
      if (!review) throw new Error('Review not found');

      // 2. Build context for AI transformation
      const companyName = review?.company_settings?.company_name || 'Organization';
      const industry = review?.company_settings?.industry || 'General';
      const orgType = review?.company_settings?.organization_type || 'private';
      const employeeCount = review?.company_settings?.employee_count || 0;
      const starRating = review?.star_rating || 0;
      const reviewText = review?.review_text || '';
      const usageDays = review?.system_usage_period_days || 0;
      const complianceMetrics = review?.compliance_success_metrics || {};

      // 3. Build AI prompt for structured case study generation
      const systemPrompt = `You are an expert case study writer specializing in HR compliance and workforce management solutions in Papua New Guinea. Transform raw client reviews into professional, compelling case studies that highlight compliance improvements, cost savings, and ROI. Use PNG Kina (K) for all currency values.`;

      const userPrompt = `Transform this client review into a professional case study:

Company Information:
- Organization: ${companyName}
- Industry: ${industry}
- Type: ${orgType}
- Employees: ${employeeCount}
- System Usage: ${usageDays} days
- Overall Rating: ${starRating}/5 stars

Raw Review:
"${reviewText}"

Compliance Success Metrics:
- Cost Savings: K${complianceMetrics?.cost_savings_kina || 0}
- Time Efficiency Gain: ${complianceMetrics?.time_efficiency_gain_percentage || 0}%
- Regulatory Adherence Score: ${complianceMetrics?.regulatory_adherence_score || 0}%
- Compliance Violations Prevented: ${complianceMetrics?.compliance_violations_prevented || 0}
- Processing Time Reduction: ${complianceMetrics?.processing_time_reduction_hours || 0} hours
- Error Rate Reduction: ${complianceMetrics?.error_rate_reduction_percentage || 0}%

Generate a comprehensive case study with the following structure:
1. Executive Summary (2-3 sentences)
2. Challenge (what problems they faced before)
3. Solution (how PNG HR Analytics helped)
4. Implementation Timeline
5. Quantifiable Results (with specific metrics)
6. ROI Calculation (based on cost savings and improvements)
7. Key Takeaways (3-5 bullet points)
8. Client Testimonial Quote (extract or enhance from review)

Make it professional, data-driven, and compelling for potential clients.`;

      // 4. Call OpenAI API with structured output
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'case_study_response',
            schema: {
              type: 'object',
              properties: {
                executive_summary: { type: 'string' },
                challenge: { type: 'string' },
                solution: { type: 'string' },
                implementation_timeline: { type: 'string' },
                quantifiable_results: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      metric: { type: 'string' },
                      before: { type: 'string' },
                      after: { type: 'string' },
                      improvement: { type: 'string' }
                    }
                  }
                },
                roi_calculation: {
                  type: 'object',
                  properties: {
                    total_savings_kina: { type: 'number' },
                    roi_percentage: { type: 'number' },
                    payback_period_months: { type: 'number' },
                    calculation_details: { type: 'string' }
                  }
                },
                key_takeaways: {
                  type: 'array',
                  items: { type: 'string' }
                },
                testimonial_quote: { type: 'string' },
                testimonial_author: { type: 'string' },
                testimonial_position: { type: 'string' }
              },
              required: ['executive_summary', 'challenge', 'solution', 'quantifiable_results', 'roi_calculation', 'key_takeaways', 'testimonial_quote'],
              additionalProperties: false,
            },
          },
        },
        reasoning_effort: 'high',
        verbosity: 'high',
      });

      const generatedContent = response?.choices?.[0]?.message?.content;

      if (!generatedContent) {
        throw new Error('Failed to generate case study content');
      }

      const caseStudy = JSON.parse(generatedContent);

      // 5. Store the generated case study
      const { data: savedCaseStudy, error: saveError } = await supabase
        ?.from('client_reviews')
        ?.update({
          case_study_content: caseStudy,
          case_study_generated_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', reviewId)
        ?.select()
        ?.single();

      if (saveError) throw saveError;

      return {
        success: true,
        caseStudy,
        reviewId,
        companyName,
        generatedAt: new Date()?.toISOString(),
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error transforming review to case study:', error);
      }
      throw new Error(errorInfo?.message);
    }
  },

  /**
   * Batch transform multiple reviews into case studies
   */
  async batchTransformReviews(reviewIds) {
    try {
      const results = [];
      const errors = [];

      for (const reviewId of reviewIds) {
        try {
          const result = await this.transformReviewToCaseStudy(reviewId);
          results?.push(result);
        } catch (error) {
          errors?.push({ reviewId, error: error?.message });
        }
      }

      return {
        success: true,
        totalProcessed: reviewIds?.length,
        successful: results?.length,
        failed: errors?.length,
        results,
        errors
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error in batch transformation:', error);
      }
      throw new Error(errorInfo?.message);
    }
  },

  /**
   * Get all case studies
   */
  async getAllCaseStudies() {
    try {
      const { data, error } = await supabase
        ?.from('client_reviews')
        ?.select(`
          *,
          company_settings (
            company_name,
            industry,
            organization_type
          )
        `)
        ?.not('case_study_content', 'is', null)
        ?.eq('is_published', true)
        ?.order('case_study_generated_at', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        companyName: row?.company_settings?.company_name,
        industry: row?.company_settings?.industry,
        organizationType: row?.company_settings?.organization_type,
        caseStudy: row?.case_study_content,
        starRating: row?.star_rating,
        generatedAt: row?.case_study_generated_at,
        createdAt: row?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching case studies:', error);
      throw error;
    }
  }
};

export default aiCaseStudyService;